import type { ReactNode } from "react";
import { useResultsHeightFloor } from "@/v2-components/pages/games/use-results-height-floor";
import { useKeepResultsInView } from "@/v2-components/pages/games/use-keep-results-in-view";
import { RESULTS_START_ANCHOR_ATTR } from "@/v2-components/pages/games/scroll-to-results";
import {
  DEFAULT_VIEW_TYPE,
  type GameViewType,
} from "@/v2-components/pages/games/game-view-type";
import { useState } from "react";
import Link from "next/link";
import ContentsNotFound from "@/components/compatibility/ContentsNotFound";
import GameCard from "@/v2-components/pages/games/GameCard";
import { Skeleton } from "@/v2-components/ui/skeleton";
import { BhapticsContent } from "@/store/reducers/contents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/v2-components/ui/select";
import SortIcon from "@/v2-components/core/icon/SortIcon";
import HorizontalViewIcon from "@/v2-components/core/icon/horizontal-view-icon";
import VerticalViewIcon from "@/v2-components/core/icon/vertical-view-icon";
import GridViewIcon from "@/v2-components/core/icon/GridViewIcon";
import { SwitchCase } from "@/v2-components/switch-case";
import { useWindowResize } from "@/hooks/useWindowResize";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { GAME_SORT_BY_FALLBACK } from "@/constants/game-sort-options";
import { useMobileFilterRowBottom } from "@/v2-components/pages/games/use-sticky-band-bottom";
import type { VrGamesFilterState } from "@/v2-components/pages/games/vr-games/use-vr-games-filters";

/**
 * `/games/vr/games` 우측 목록 (시안 `29516:64228`, 1074w).
 *
 * ## 2026-08-31 개정 시안 (#5436)
 *
 * 화면이 `29497:63500` → `29889:100934` 로 개정됐다.
 *
 * ★**「카드 구조는 유지됐다」고 적어 뒀는데 틀렸다.** 종전 주석이 스스로 *"아래 시안
 * 참조는 옛 판 id 라 확인하지 않았다"* 고 밝히면서 동시에 *"구조는 유지"* 로 결론냈다 —
 * **안 본 것을 근거로 한 판정**이다. 새 판(`29889:101031`)을 실제로 조회하니 카드가
 * 통째로 다시 그려져 있었다. 아래 표가 실측이고, id 도 새 판으로 갱신했다.
 *
 * ## 카드 — 박스에서 **테두리 없는 구조**로 (`29889:101031`)
 *
 * ```
 *                    옛 판(구현돼 있던 것)          개정 시안
 *   카드 바깥      border + rounded-16 + 그림자     없음 (그냥 세로 스택 gap 12)
 *   썸네일         하단 경계선 + 어두운 그라디언트    radius 8 만
 *   캡션           px-16 py-14                     패딩 없음 (본문에 붙는다)
 *   제목           15/18 SemiBold #121619          16/22 **Medium** #21272a
 *   지원타입 배지   썸네일 **우상단**으로 미러링       **제목 앞 인라인**
 *   플랫폼         보더 pill(10.5px)                아이콘 20 + 12/16 (칩 아님)
 * ```
 *
 * 즉 종전에 시안을 맞추려고 넣었던 우회 세 개가 **전부 불필요해졌다** — 배지 미러링 ·
 * pill 자식 선택자 · 안쪽 라운드 지우기. 개정 시안이 오히려 `GameCard` 기본값과 맞다:
 * `Root` 가 `gap-3`(12) · `Image` 가 `rounded-[8px]` · `Title` 이 배지를 제목 앞에
 * 인라인으로 붙이고 · `Badge size="sm"` 이 `12/16 SemiBold · px-6 · py-2 · h-20 ·
 * radius 4` 로 시안과 **같은 값**이다. 그래서 덮는 것은 글자 크기·굵기 둘뿐이다.
 *
 * ⚠️ `GameCard` 는 소비처가 **22곳**이라 공유 컴포넌트는 고치지 않는다 —
 * 이 호출처에서만 `className` 으로 덮는다.
 *
 * ## 시안 안에 카드가 **두 종류**다 — 실물 인스턴스가 정본
 *
 * 그리드에 `game-PUBG: BATTLEGROUNDS` 1장과 `gcard` 여러 장이 섞여 있고 값이 다르다
 * (데스크탑 246 vs 218 · 모바일 215 vs 220). `gcard` 쪽을 따라가면 **모바일 플랫폼이
 * 아이콘 없는 맨 텍스트**가 된다. 셋으로 갈랐다 — `gcard` 는 `gvis` 가 **빈 프레임**이고,
 * 배지가 컴포넌트가 아니라 **손으로 그린 프레임 + 텍스트**이며, 플랫폼에 아이콘
 * 인스턴스가 없다. 즉 디자이너가 채운 **자리 표시**다. 시안 렌더에서도 첫 장만 실제
 * 썸네일·아이콘이 나온다. 그래서 `game-PUBG` 를 정본으로 읽었다.
 *
 * **게임 키 배너(`ResellBanner`)는 붙이지 않는다** — 클릭하면 `SelectGameKeyDialog` 가
 * 열려서 목록 훑는 흐름을 끊는다. 게임 키는 레일의 「게임 키 제공」 필터로 거른다.
 *
 * ## 정렬
 *
 * 서버 정렬이다(`useGames` 의 `sortBy` 가 쿼리로 나간다). 필터는 클라에서 걸지만
 * 정렬은 전량을 받아오는 기준 자체라 서버가 맞다.
 */
export default function VrGamesList({
  state,
  emptySlot,
}: {
  state: VrGamesFilterState;
  /**
   * ⑧ 빈 상태 — 페이지가 만들어 내려보낸다 (#5599 · 2026-09-08).
   *
   * 여기서 만들지 않는 이유는 **투표·로그인 배선이 페이지에 있어서**다(드롭다운과 같은
   * 이유). 안 넘기면 범용 `ContentsNotFound` 로 떨어진다 — 그러면 드롭다운은
   * 「PC 에서 지원」이라 말하는데 그리드는 **아무 말도 안 하는** 상태가 된다.
   */
  emptySlot?: ReactNode;
}) {
  const { t } = useTrans(["games"]);
  const { games, isLoading } = state;
  /**
   * 결과 툴바가 붙을 y — 모바일 검색줄의 바닥. 데스크탑은 0 이고 `laptop:static` 이라
   * 읽히지 않는다 (#5561).
   */
  const toolbarTop = useMobileFilterRowBottom();
  /**
   * 보기 모드 — 도입 당시 **develop 에 이미 있던 기능**이다(레거시 `GameCardList` 의
   * `ViewTypeToggle` · #5689 가 삭제). 이 화면만 빠져 있었다. 순환 순서·기본값·리사이즈
   * 처리·아이콘 3종을 그쪽에서 **그대로** 가져왔고, 지금 그 값들의 정본은
   * `game-view-type.ts` 다:
   *
   * ```
   *   horizontal  한 줄에 1장 · 가로 썸네일 위 + 텍스트 아래   (시안 기본)
   *   vertical    한 줄에 1장 · 세로 썸네일 좌 + 텍스트 우
   *   grid        한 줄에 3장 · 세로 썸네일 + 짧은 제목
   * ```
   *
   * ⚠️ `GameCardItem` 을 그대로 쓰지 않는다 — 그 변형들은 `ResellBanner`·`Devices`·
   * `Resell` 을 함께 그리는데, 이 화면은 **의도적으로 뺐다**(눌리면 `SelectGameKeyDialog`
   * 가 열려 목록 훑는 흐름이 끊긴다 — 아래 주석). 카드 표기도 개정 시안 쪽이다.
   * 그래서 **토글과 아이콘만** 재사용하고 카드는 이 파일 것을 쓴다.
   */
  /**
   * 결과가 줄 때 문서가 무너져 스크롤이 되당겨지는 것을 막는다 (사용자 제보:
   * *"검색시에 튀는데"*). 상세는 훅 docblock.
   */
  const { outer: floorOuter, inner: floorInner } = useResultsHeightFloor(
    games.length,
  );
  /**
   * 결과가 줄어 **화면 밖에 남으면** 결과 상단으로 되돌린다 (#5676). 위 바닥과 축이
   * 겹치므로(둘 다 「결과가 줄 때의 스크롤」) 역할을 나눠 둔다 — 바닥은 **타이핑 중
   * 과도기**의 되당김을, 이쪽은 **정착 후** 결과가 통째로 화면 위에 남는 것을 맡는다.
   */
  /**
   * ★**타이핑이 아니라 「확정」에 반응한다** (#5672).
   *
   * 종전 `games.length` 는 그리드가 `draft` 로 라이브 필터하므로 **글자마다** 바뀌었다
   * → 리스트 중간에서 한 글자만 쳐도 리스트 시작으로 되돌아갔다. 요구사항은 확정
   * (`#5609` — *"검색 버튼 눌렀을때"*)에만 있다. 키의 정의·실측은
   * `use-*-games-filters.ts` 의 `committedKey` 주석에 있다.
   */
  useKeepResultsInView(state.committedKey);
  const [viewType, setViewType] = useState<VrViewType>(DEFAULT_VIEW_TYPE);
  /**
   * 태블릿 이상에서는 시안대로 가로 카드만 쓴다.
   *
   * ★**이 숫자와 토글을 숨기는 숫자가 같아야 한다.** 종전엔 리셋이 `> 768`(기존 구현에서
   * 그대로 가져옴)인데 숨김만 `laptop:hidden`(**1024**)이라 짝이 깨져 있었다 —
   * `tailwind.config.ts` 에서 `smd`=768 · `laptop`=1024 로 **다른 값**이다.
   *
   * 그 사이(768~1023)에서 **컨트롤은 보이는데 선택이 안 붙었다.** 실측(900px):
   * 마운트 `column` → 클릭 `row` → 리사이즈 한 번에 다시 `column`. `useWindowResize` 는
   * 방향 전환·창 크기 변경·주소창 접힘에도 불리므로 「눌렀는데 풀린다」로 보인다.
   *
   * ★**부등호도 맞춰야 한다.** `smd:hidden` 은 `min-width:768` = **768 포함**인데 리셋이
   * `> 768`(=769부터)이면 **정확히 768 한 칸**에서 같은 일이 난다. 실측: 767 에서
   * `vertical` 을 고르고 768 로 넓히면 토글은 숨었는데 선택이 `row` 로 남아 되돌릴
   * 방법이 없었다. 그래서 `>= 768` 이다.
   *
   * ⚠️ 기존 구현(레거시 `GameCardList`)에 그 1px 이 남아 있었는데(`smd:hidden` + `> 768`)
   * **#5660 리뷰 f1 에서 함께 맞췄다**(`>= 768`). 이 주석이 그 사실을 적어 두고도
   * 고치지 않은 자리였고 리뷰가 그걸 짚었다. 그때 세 파일이 같은 부등호가 됐고, 지금은
   * #5689 가 ~~`GameCardList.tsx`~~ 를 지워 **두 파일**(PC·VR 목록)이다 — 가드가 **소스에서
   * 파생한 집합**으로 둘을 함께 잠근다(`game-view-type-default.test.ts`).
   *
   * **768 로 맞췄다**(토글은 `smd:hidden`). 이 화면이 내세운 목표가 「두 화면에서 같게
   * 돈다」이고, 768 부터는 그리드가 이미 `tablet:grid-cols-2` 라 모바일 밀도 3종이
   * 들어맞지 않는다 — 세 모드가 모두 2장/줄이 돼 **개수로는 구분도 안 된다**(실측).
   */
  useWindowResize(() => {
    if (window.innerWidth >= 768) setViewType("horizontal");
  });

  return (
    /**
     * 시안 `29889:101020`(lp-right) — x=384 w=1056 인데 **자식은 전부 x=20 w=1036** 이다.
     * 즉 왼쪽만 20 들어가고 오른쪽은 0 이라 그리드 오른끝이 컨테이너 끝(1440)에 붙는다.
     * 그래서 레일↔그리드 실제 간격은 24 가 아니라 **44**(= `gap-6` + 이 `pl-5`)다.
     * 검산: 레일 360 + 44 + 그리드 1036 = 1440 · 그리드 1036 = 334×3 + 17×2.
     * 모바일엔 이 인셋이 없다(`29942:17614` grid x=16 = 컨테이너 여백 그대로).
     */
    <div ref={floorOuter} className="w-full min-w-0 laptop:pl-5">
      {/**
       * lp-bar — 데스크탑 `29889:101021` h=24 · 모바일 `29942:17607`(Toobar) h=24.
       *
       * ★**정렬이 pill 이 아니다.** 종전 구현은 옛 판(`29516:64232`)의
       * `rounded-999` + 보더 칩이었는데, 개정 시안은 **아이콘 24 + 라벨**만 있고
       * 배경·보더가 없다(시안 렌더로 확인). 높이도 34 → 24 로 줄었다.
       *
       * 오른쪽 끝의 목록/그리드 전환(`29942:17613`)은 아래 `ViewTypeToggle` 이 그린다.
       * ★한때 *"이 화면에 보기 모드 개념이 없다"* 로 빼고 #5464 로 미뤘는데 **틀렸다** —
       * `GameCardList` 의 `ViewTypeToggle` 로 **develop 에 이미 있는 기능**이었고 이 화면만
       * 이식이 빠져 있었다(그 이슈는 닫았다).
       */}
      {/**
       * ★**모바일에서 검색줄 아래에 함께 고정된다** (#5561 · 작성자 요청
       * *"아래에 있는 게임수와 정렬/보기모드까지도 스티키로 노출필요"*).
       *
       * `top` 은 검색줄의 **붙었을 때 바닥**이다 — 두 층 다 동적이라 상수를 못 쓴다
       * (사유·실측은 `useMobileFilterRowBottom`).
       *
       * ⚠️ **배경이 있어야 한다.** 없으면 카드가 이 줄 **뒤로 비쳐 지나간다**.
       * ⚠️ **여백은 `py-2`(8) 뿐이다.** 검색줄이 이미 `py-3` 로 아래 12 를 갖고 있어
       *    붙었을 때 둘 사이 간격은 12+8 = 20 이 된다. 더 주면 목록이 밀린다.
       * ⚠️ `-mx-*` 로 좌우를 넓히지 않는다 — 부모 여백 안에서 배경만 깔면 카드가
       *    양옆으로 새어 보이지 않는다(그리드도 같은 여백 안이라 폭이 같다).
       *
       * 데스크탑은 `laptop:static` 이다. 검색줄이 `laptop:hidden` 이라 쌓을 층이 없고,
       * `useMobileFilterRowBottom` 도 그 폭에서 0 을 준다 — **두 조건이 같은 방향**이다.
       *
       * ⚠️ **이 줄은 `data-games-results` 앵커이기도 하다.** sticky 가 되면서
       *    `scrollToResults` 의 전제(앵커가 흐름에 있다)가 깨졌다 — VR 에 그 정렬을 붙일 때
       *    (#5609)는 `scroll-to-results.ts` 의 `RESULTS_ANCHOR_ATTR` 주석을 먼저 볼 것.
       */}
      {/**
       * ★**흐름 기준 착지점** (#5730). 아래 줄은 모바일에서 `sticky` 라 붙으면
       * `rect.top` 이 고정돼 정렬이 못 움직인다 — 그래서 **흐름에 있는** 표식을
       * 바로 위에 둔다. 높이 0 이라 레이아웃을 안 바꾼다.
       */}
      <div
        {...{ [RESULTS_START_ANCHOR_ATTR]: "" }}
        aria-hidden
        className="h-0"
      />
      <div
        className="sticky z-[9] flex h-auto items-center justify-between bg-white py-2 laptop:static laptop:h-6 laptop:py-0"
        style={{ top: toolbarTop }}
        data-games-results
      >
        {/**
         * ★**개수 문구는 두 브레이크포인트가 굵기·색까지 다르다.**
         *
         * ```
         *              모바일 `29942:17608`        데스크탑 `29889:101022`
         *   크기       12 / 16                     14 / 18
         *   굵기       **Regular(400)**            **SemiBold(600)**
         *   색         `#4d5358` coolgray-70       `#121619` coolgray-100
         *   자간       0.4                         0.25
         * ```
         *
         * 종전엔 데스크탑 값(600 · coolgray-100)을 양쪽에 쓰고 크기만 갈랐다 —
         * 좌표만 보고 크기 차이만 읽었기 때문이다. 굵기·색은 metadata 에 안 나온다.
         *
         * 숫자는 **필터 적용 후** 개수다.
         */}
        <p className="text-sm font-400 tracking-[0.4px] text-coolgray-70 laptop:text-base laptop:font-600 laptop:tracking-[0.25px] laptop:text-coolgray-100">
          {t("{{count}} games", { count: games.length })}
        </p>

        {/**
         * 시안 `29889:101023`·`29942:17609` `Right` — 개수는 왼쪽에 두고 **정렬과 보기
         * 전환을 오른쪽에 한 묶음**으로 둔다. 묶음 안 간격은 데스크탑 24 · 모바일 16.
         *
         * ★`justify-between` 만으로는 안 된다 — 그러면 정렬과 토글이 양 끝으로 벌어진다
         * (실측 72, 시안 16).
         */}
        <div className="flex items-center gap-4 laptop:gap-6">
          <Select value={state.sortBy} onValueChange={state.setSortBy}>
            {/* 시안 `29889:101024`·`29942:17610` — 아이콘 24 + 간격 4 + 라벨.
              보더·배경 없음. 라벨은 양쪽 다 **Regular** `#21272a`(coolgray-90 = 기본값)이고
              크기·자간만 갈린다(12/0.4 → 14/0.25). */}
            <SelectTrigger className="h-6 w-auto gap-1 p-0 text-sm font-400 tracking-[0.4px] laptop:text-base laptop:tracking-[0.25px]">
              <SortIcon className="h-6 w-6 shrink-0" />
              {t(
                GAME_SORT_BY_FALLBACK.options.find(
                  (o) => o.value === state.sortBy,
                )?.description ?? "",
              )}
            </SelectTrigger>
            <SelectContent>
              {/* 정렬 목록도 손으로 안 적는다 — 폴백 상수가 단일 출처다. */}
              {GAME_SORT_BY_FALLBACK.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.description)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/**
           * 시안 `29942:17613` — `Right` 묶음의 **가장 오른쪽**(x=304), Sort 와 간격 16.
           * 데스크탑 시안(`29889:101027`)에선 `hidden` 이다.
           *
           * ★숨기는 경계는 **`smd`(768)** 이고 위 리셋(`innerWidth >= 768`)과 **같은 폭**을
           * 가려야 한다. **부등호까지 같아야 한다** — `smd:hidden` 은 `min-width:768` 이라
           * 768 을 **포함**하므로 리셋이 `> 768` 이면 그 한 칸에서 토글만 사라진다.
           * 사유·실측은 그 훅 주석에. 레거시 구현(`GameCardList`)도 `smd:hidden` 이었다
           * (#5689 가 삭제 — 지금 이 경계를 잠그는 것은
           * `__tests__/v2-components/game-view-type-default.test.ts` 다).
           */}
          <ViewTypeToggle
            viewType={viewType}
            onToggle={setViewType}
            label={t("Change view")}
          />
        </div>
      </div>

      {!isLoading && games.length === 0 && (emptySlot ?? <ContentsNotFound />)}

      {/**
       * 그리드 — 시안 좌표에서 낸 값이다.
       *
       * ```
       *   lp-bar→그리드   데스크탑 `101021` 끝 24 → `101028` y=44   = 20
       *                   모바일   `17607` 끝 180 → `17614` y=196   = 16
       *   행 간격         데스크탑 grid-row y 0·278·556 (h 246)     = 32
       *                   모바일   gcard y 239·483·727 (h 215/220)  = 24
       *   열 간격         데스크탑 카드 x 0·351·702 (w 334)         = 17
       * ```
       *
       * 검산: 334×3 + 17×2 = **1036** = 시안 그리드 폭.
       * ⚠️ 열 17 은 임의값이 아니라 1036 을 3등분한 결과라 16 으로 반올림하면 폭이 안 맞는다.
       * 태블릿 2열은 시안에 없다 — 기존 동작을 유지한다.
       */}
      <div
        ref={floorInner}
        className="no-scroll-anchor mt-4 grid grid-cols-3 gap-x-4 gap-y-6 tablet:grid-cols-2 laptop:mt-5 laptop:grid-cols-3 laptop:gap-x-[17px] laptop:gap-y-8"
      >
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            // 스켈레톤도 카드와 **같은 골격**이라야 로딩 → 완료에서 그리드가 안 튄다.
            // 카드가 테두리 없는 스택으로 바뀌었으므로 여기도 같이 바꾼다
            // (안 바꾸면 로딩 동안만 박스가 보이고 완료 시 사라진다).
            <div
              key={i}
              className="col-span-3 flex flex-col gap-3 tablet:col-span-1"
            >
              <Skeleton className="aspect-game-card-horizontal w-full rounded-[8px]" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-[22px] w-2/3" />
                <Skeleton className="h-5 w-1/2" />
              </div>
            </div>
          ))}

        {!isLoading &&
          games.map((game) => (
            <Card key={game.id} game={game} viewType={viewType} />
          ))}
      </div>
    </div>
  );
}

/**
 * 카드 한 장 (시안 `29889:101031` 데스크탑 334×246 · `29942:17615` 모바일 328×215).
 *
 * 개정 내용과 「왜 덮는 것이 둘뿐인가」는 파일 상단 주석 참조.
 *
 * ```
 *   썸네일  334×156 / 328×153  → 비율 2.141 / 2.144
 *   갭      12  (Root 기본 `gap-3`)
 *   캡션    갭 8: [배지 + 제목 16/22 Medium]  /  [플랫폼 아이콘20 + 12/16]
 * ```
 *
 * 비율은 기존 토큰 `aspect-game-card-horizontal`(342/160 = **2.138**)을 그대로 쓴다 —
 * 시안 두 값과 0.2% 안쪽이라 새 토큰을 만들 이유가 없다.
 *
 * ## 상태 배지와 지원타입 배지가 이제 안 겹친다
 *
 * 종전엔 시안이 지원타입을 썸네일 **좌상단**에 둬서 리포의 상태 배지(New/Upcoming) 자리와
 * 충돌했고, 그래서 지원타입을 우상단으로 미러링했다. 개정 시안은 지원타입을 **제목 앞
 * 인라인**으로 옮기고 좌상단은 `Extra`(New/Best)에 내줬다 — 리포의
 * `GameCard.Image` 배지 슬롯(`absolute inset-0 p-2`, 간격 4)과 **좌표까지 같다**.
 * 미러링을 걷어낸 이유다.
 */
function Card({
  game,
  viewType = "horizontal",
}: {
  game: BhapticsContent;
  viewType?: VrViewType;
}) {
  /**
   * 보기 모드별 골격. **시안 카드 표기(테두리 없음 · 16/22 Medium · 아이콘 20 + 12/16)는
   * 셋 다 그대로**고, 바뀌는 건 썸네일 방향과 배치뿐이다.
   *
   * `col-span` 이 밀도를 정한다 — 컨테이너가 모바일 3열이라 `3` 이면 한 줄에 1장,
   * `1` 이면 3장이다. 태블릿 이상은 `tablet:col-span-1` 로 되돌려 시안 3열을 지킨다.
   */
  if (viewType === "vertical") {
    return (
      <GameCard game={game} className="col-span-3 tablet:col-span-1">
        <Link
          href={`/games/experiences/${game.slug}`}
          className="group flex flex-row gap-3"
        >
          {/* 세로 썸네일 — 기존 구현과 같은 높이(148). */}
          <GameCard.Image
            direction="vertical"
            className="h-[148px] shrink-0 transition-opacity group-hover:opacity-90"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <GameCard.Title className="text-lg font-500" />
            <GameCard.Platforms className="tracking-[0.4px] laptop:text-sm" />
          </div>
        </Link>
      </GameCard>
    );
  }

  if (viewType === "grid") {
    return (
      <GameCard game={game} className="col-span-1">
        <Link
          href={`/games/experiences/${game.slug}`}
          className="group flex flex-col gap-2"
        >
          <GameCard.Image
            direction="vertical"
            className="w-full transition-opacity group-hover:opacity-90"
          />
          {/* 3열이라 폭이 좁다 — 제목 한 줄로 자르고 플랫폼은 뺀다(아이콘이 안 들어간다). */}
          <GameCard.Title className="line-clamp-1 text-base font-500" />
        </Link>
      </GameCard>
    );
  }

  return (
    <GameCard game={game} className="col-span-3 tablet:col-span-1">
      {/* 시안 `29889:101031` — 썸네일↔캡션 12. */}
      <Link
        href={`/games/experiences/${game.slug}`}
        className="group flex flex-col gap-3"
      >
        <GameCard.Image
          direction="horizontal"
          className="w-full transition-opacity group-hover:opacity-90"
        />

        {/* 시안 `29889:101033` — 제목행↔플랫폼 8. */}
        <div className="flex flex-col gap-2">
          {/**
           * 시안 `29889:101036` — 16/22 **Medium(500)** `#21272a`.
           * 기본값은 `text-base font-600 ... laptop:text-lg`(모바일 14 · SemiBold)이라
           * 크기·굵기 둘을 덮는다. 모바일 시안도 h=22 라 **양쪽 다 16/22** 다.
           */}
          <GameCard.Title className="text-lg font-500" />
          {/**
           * 시안 `29889:101037` — 아이콘 20 + 간격 4 + **12/16** Regular `#4d5358`,
           * 줄바꿈 간격 8. 칩(보더·배경)이 **없다**.
           *
           * 기본값이 이미 아이콘 20 · `gap-1` · `gap-2` · `text-coolgray-70` 라 맞는데
           * **`laptop:text-base`(14) 로 커지는 것만** 다르다 — 시안은 데스크탑도 12 다.
           */}
          <GameCard.Platforms className="tracking-[0.4px] laptop:text-sm" />
        </div>
      </Link>
    </GameCard>
  );
}

/** 정본 `game-view-type.ts` 의 **같은 세 값**이다 — 새로 정의하지 않는다. */
type VrViewType = GameViewType;

/**
 * 보기 전환 (시안 `29942:17613` · 원형은 레거시 `GameCardList` 의 `ViewTypeToggle`,
 * #5689 가 삭제).
 *
 * 순환 순서를 **PC 목록과 같게** 둔다 — 두 화면에서 같은 아이콘을 눌렀는데 다른 순서로
 * 돌면 같은 기능으로 안 읽힌다. 아이콘도 그 셋을 그대로 쓴다.
 */
function ViewTypeToggle({
  viewType,
  onToggle,
  label,
}: {
  viewType: VrViewType;
  onToggle: (t: VrViewType) => void;
  label: string;
}) {
  const next: Record<VrViewType, VrViewType> = {
    horizontal: "vertical",
    vertical: "grid",
    grid: "horizontal",
  };

  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => onToggle(next[viewType])}
      className="shrink-0 text-coolgray-90 smd:hidden"
    >
      <SwitchCase
        value={viewType}
        caseBy={{
          horizontal: <HorizontalViewIcon className="h-6 w-6" />,
          vertical: <VerticalViewIcon className="h-6 w-6" />,
          grid: <GridViewIcon className="h-6 w-6" />,
        }}
      />
    </button>
  );
}
